//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type Ref } from '@hcengineering/core'

import { type Document, type Teamspace } from './types'

/**
 * MIME type the navigator marks its document drags with.
 *
 * A drag between two teamspaces crosses two independent navigator components,
 * so the drop needs a way to recognise the drag as ours before it reads any
 * shared state — and, in Firefox, a way to recover the source document at all.
 *
 * @public
 */
export const documentDragMimeType = 'application/vnd.huly.document+json'

/**
 * The dragged document as it travels on the `DataTransfer`.
 *
 * Only identifiers: the drop reads the document itself from the client, so a
 * stale payload can never resurrect a document that changed mid-drag.
 *
 * @public
 */
export interface DocumentDragPayload {
  document: Ref<Document>
  space: Ref<Teamspace>
}

/**
 * The slice of `DataTransfer` this module needs.
 *
 * Declared structurally rather than as the DOM type so the payload helpers stay
 * usable — and testable — where no DOM is around.
 *
 * @public
 */
export interface DocumentDragData {
  setData: (format: string, data: string) => void
  getData: (format: string) => string
  readonly types: readonly string[]
}

/** @public */
export function encodeDocumentDragPayload (payload: DocumentDragPayload): string {
  return JSON.stringify(payload)
}

/**
 * Read a payload back, or `undefined` when the data is not ours.
 *
 * Anything at all can be dropped onto the navigator — files, selected text, a
 * drag from another application — so this never throws: unparseable or foreign
 * data simply is not a document drag.
 *
 * @public
 */
export function parseDocumentDragPayload (raw: string | null | undefined): DocumentDragPayload | undefined {
  if (raw == null || raw === '') return undefined

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return undefined
  }

  if (typeof parsed !== 'object' || parsed === null) return undefined

  const { document, space } = parsed as Record<string, unknown>
  if (typeof document !== 'string' || document === '') return undefined
  if (typeof space !== 'string' || space === '') return undefined

  return { document: document as Ref<Document>, space: space as Ref<Teamspace> }
}

/**
 * Put the dragged document on the transfer.
 *
 * Firefox cancels a drag whose `dragstart` handler left the `DataTransfer`
 * empty, so this call is what makes the feature work there at all. The custom
 * type carries the payload; `text/plain` carries the title, so a drop that
 * lands outside the navigator — in the editor, in another application — pastes
 * something meaningful instead of a blob of JSON.
 *
 * @public
 */
export function setDocumentDragPayload (data: DocumentDragData, payload: DocumentDragPayload, title: string): void {
  data.setData(documentDragMimeType, encodeDocumentDragPayload(payload))
  data.setData('text/plain', title)
}

/**
 * Whether the drag in flight is a navigator document drag.
 *
 * During `dragover` browsers expose the transfer's *types* but refuse to expose
 * its *data*, so this is the only thing a drop target can ask before the drop
 * actually happens.
 *
 * @public
 */
export function hasDocumentDragPayload (data: Pick<DocumentDragData, 'types'> | null | undefined): boolean {
  return data != null && Array.from(data.types).includes(documentDragMimeType)
}

/** @public */
export function readDocumentDragPayload (data: DocumentDragData | null | undefined): DocumentDragPayload | undefined {
  if (data == null) return undefined
  return parseDocumentDragPayload(data.getData(documentDragMimeType))
}

/**
 * Why the navigator refuses a drop.
 *
 * @public
 */
export type DocumentDropRefusal = 'self' | 'subtree' | 'archived' | 'forbidden'

/**
 * What the drop gate needs to know about the teamspace under the cursor.
 *
 * @public
 */
export interface TeamspaceDropAccess {
  archived: boolean
  /** Whether the account may put documents into this teamspace. */
  canAddDocuments: boolean
}

/**
 * The half of the gate that depends only on the teamspace under the cursor.
 *
 * Split out because `dragover` sometimes runs before the dragged document is
 * known — a drag whose in-page state was never set still has to be refused by
 * an archived or read-only teamspace.
 *
 * @public
 */
export function getTeamspaceDropRefusal (access: TeamspaceDropAccess): DocumentDropRefusal | undefined {
  if (access.archived) return 'archived'
  if (!access.canAddDocuments) return 'forbidden'

  return undefined
}

/**
 * Whether `dragged` may be dropped onto `target`, and why not when it may not.
 *
 * `subtree` is the dragged document together with its descendants, as built by
 * `collectSubtreeRefs`. Both the self and the subtree case would close a
 * `parent` cycle and strand the documents involved, so they are checked first:
 * they hold no matter how permissive the teamspace is, and reporting the
 * teamspace instead would send the user off fixing the wrong thing.
 *
 * This gate is UX only. It decides what the navigator highlights and what it
 * lets go of; the move itself is authorized on the server, where the permission
 * middleware rejects the whole transaction. Anything this function allows may
 * still come back refused, and the caller has to be ready for that.
 *
 * @public
 */
export function getDocumentDropRefusal (
  dragged: Ref<Document>,
  subtree: ReadonlySet<Ref<Document>>,
  target: Ref<Document>,
  access: TeamspaceDropAccess
): DocumentDropRefusal | undefined {
  if (target === dragged) return 'self'
  if (subtree.has(target)) return 'subtree'

  return getTeamspaceDropRefusal(access)
}

/**
 * Shorthand for {@link getDocumentDropRefusal} where the reason does not matter.
 *
 * @public
 */
export function canDropDocument (
  dragged: Ref<Document>,
  subtree: ReadonlySet<Ref<Document>>,
  target: Ref<Document>,
  access: TeamspaceDropAccess
): boolean {
  return getDocumentDropRefusal(dragged, subtree, target, access) === undefined
}
